//
// # SrvPanel
//
// Panel showing SRV (Schematic Rules Validation) results for the active schematic.
// Modeled on DrcPanel, but its state lives in `workspace.srv` (an SrvModel)
// rather than a server round-trip: every rule always runs, and every violation
// is an error (no severity distinction).
//
// The header carries the Run button and the "Show On Canvas" toggle alongside
// the title, then two stacked sections below:
//   - Results: the violation list grouped by rule, with click-to-focus rows
//              kept in sync with the canvas arrow selection. An edit since the
//              last run swaps the summary for an "out of date" banner, but
//              changes nothing else: the rows and arrows stay live so a run can
//              be worked through fix by fix.
//   - Rules:   a collapsed, read-only "Rules that will run" reference list
//              (no checkboxes — rule selection does not exist).
//
// The Run button deliberately carries no pass/fail tint or status icon of its
// own: it uses the same neutral styling as "Run DRC" / "Run LVS" so the four
// analytics panels agree, and the run's outcome is reported by the status
// banner at the top of the results list, exactly as it is in those panels.
//
// The panel forwards its Dockview visibility to the model (`setPanelVisible`),
// which is what gates the canvas overlay.
//

import React, { useEffect, useRef, useState } from "react";
import type { DockviewPanelApi } from "dockview-core";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  GitMerge,
  HelpCircle,
  Play,
  XCircle,
} from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import type { SrvViolation } from "../../services/SrvModel";
import { createSrvLogger } from "../../services/srvLogger";

export function SrvPanel({ api }: { api: DockviewPanelApi }) {
  const workspace = useWorkspace();
  // Subscribe to workspace changes so the panel re-renders whenever the model
  // calls its onChange (which triggers updateSnapshot).
  useWorkspaceSnapshot();
  const srv = workspace.srv;
  const state = srv.getState();

  // The SRV logger's lifetime is tied to this panel: created on mount, disposed
  // (flushing its final interaction batch) on unmount.
  useEffect(() => {
    const srvLogger = createSrvLogger();
    srv.registerLogger(srvLogger);
    return () => {
      srvLogger.dispose();
      srv.registerLogger(null);
    };
  }, [srv]);

  // Forward Dockview visibility to the model, and mark hidden on unmount.
  useEffect(() => {
    srv.setPanelVisible(api.isVisible);
    const disposable = api.onDidVisibilityChange(
      ({ isVisible }: { isVisible: boolean }) => {
        srv.setPanelVisible(isVisible);
      }
    );
    return () => {
      disposable.dispose();
      srv.setPanelVisible(false);
    };
  }, [api, srv]);

  const [rulesExpanded, setRulesExpanded] = useState(false);

  const lastRun = state.lastRun;

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Header — title on the left, Run button + canvas toggle on the right. */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <GitMerge size={11} />
            Check & Save Results
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RunButton
              isRunning={state.isRunning}
              schematicActive={state.schematicActive}
              onRun={() => {
                void srv.runSrv();
              }}
            />
            <ShowOnCanvasToggle
              checked={state.highlightsEnabled}
              // Nothing to show before a run; the toggle would be a no-op.
              // A stale run still has arrows to toggle, so it stays enabled.
              disabled={lastRun === null}
              onChange={(checked) => {
                srv.setHighlightsEnabled(checked);
              }}
            />
          </div>
        </div>
      </div>

      {/* Stacked sections: Results on top, Rules reference list at the bottom. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <ResultsTab />

        <RulesExpander
          rules={state.rules}
          expanded={rulesExpanded}
          onToggleExpanded={() => {
            setRulesExpanded((e) => !e);
          }}
        />
      </div>
    </div>
  );

  // ResultsTab is declared inline so it closes over `srv` / `state` without
  // re-plumbing the model through props.
  function ResultsTab() {
    if (!lastRun) {
      return (
        <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
          No results yet. Run Check & Save using the button above.
        </div>
      );
    }

    const violations = lastRun.violations;
    const total = violations.length;
    const passed = total === 0;
    const elapsedMs = lastRun.stats.reduce((sum, s) => sum + s.elapsedMs, 0);
    const groups = groupByRule(violations);

    // Once the design has been edited the run describes the pre-edit design.
    // The banner says so, replacing the pass/fail summary rather than sitting
    // alongside it, so the panel never reports a stale verdict as current — but
    // that is all it does. The list stays fully live and the arrows stay on the
    // canvas: working through the results means fixing one violation and
    // jumping to the next, and a re-run between every fix would defeat that.
    const stale = state.resultsStale;

    return (
      <div className="p-2">
        {/* Status banner */}
        {stale ? (
          <div className="px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] bg-amber-500/10 text-amber-400">
            <AlertTriangle size={14} />
            Results are out of date — re-run
          </div>
        ) : (
          <div
            className={`px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] ${
              passed
                ? "bg-green-500/10 text-green-400"
                : "bg-red-500/10 text-red-400"
            }`}
          >
            {passed ? <CheckCircle size={14} /> : <XCircle size={14} />}
            {passed
              ? "Checks passed"
              : `${String(total)} violation${
                  total !== 1 ? "s" : ""
                } in ${String(groups.length)} rule${
                  groups.length !== 1 ? "s" : ""
                } · ${String(elapsedMs)} ms`}
          </div>
        )}

        {/* Grouped violation list */}
        <div className="flex flex-col gap-2">
          {groups.map((group) => (
            <div key={group.ruleId} className="flex flex-col gap-1">
              <div className="px-1 text-[10px] font-medium uppercase tracking-wide text-[var(--color-ide-text-muted)]">
                {ruleLabel(state.rules, group.ruleId)} ({group.items.length})
              </div>
              {group.items.map(({ violation, index }) => (
                <ViolationRow
                  key={index}
                  violation={violation}
                  selected={state.highlightedViolationIndex === index}
                  onClick={() => {
                    srv.focusViolation(index);
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

// The Run button sits in the panel header. Styling mirrors `drcButtonClass` in
// DrcPanel (neutral surface, Play icon, dimmed when disabled) so the run
// buttons across the analytics panels read as the same control; the run's
// outcome is left to the status banner rather than tinting the button.
function RunButton(props: {
  isRunning: boolean;
  schematicActive: boolean;
  onRun: () => void;
}) {
  const { isRunning, schematicActive, onRun } = props;

  return (
    <button
      className={
        "px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
        "normal-case tracking-normal " +
        "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
        "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      }
      onClick={onRun}
      disabled={isRunning || !schematicActive}
      title={
        schematicActive
          ? "Run schematic checks and save the active schematic"
          : "Open a schematic to run Check & Save"
      }
    >
      <Play size={12} />
      {isRunning ? "Checking…" : "Check & Save"}
    </button>
  );
}

// Whether the violation arrows are drawn on the canvas. A state label on a
// checkbox rather than the action label the old bottom-of-panel button carried
// ("Hide Canvas Highlights"), so the control reads the same way round as the
// model flag it drives. `highlightsEnabled` starts true and is reset to true by
// every run, so this is checked by default without any special-casing here.
function ShowOnCanvasToggle(props: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { checked, disabled, onChange } = props;

  return (
    <label
      className={`flex items-center gap-1.5 text-[11px] normal-case tracking-normal font-normal ${
        disabled
          ? "text-[var(--color-ide-text-dim)] cursor-not-allowed"
          : "text-[var(--color-ide-text-muted)] cursor-pointer"
      }`}
      title={
        disabled
          ? "Run Check & Save to see violations on the canvas"
          : "Show violation markers on the canvas"
      }
      aria-disabled={disabled}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      Show Errors
    </label>
  );
}

// The "Rules that will run" reference list lives at the bottom of the panel:
// a collapsed, read-only expander (no checkboxes — rule selection does not
// exist).
function RulesExpander(props: {
  rules: ReadonlyArray<{
    id: string;
    label: string;
    description: string;
  }> | null;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const { rules, expanded, onToggleExpanded } = props;

  return (
    <div className="p-2">
      {/* "Rules that will run" expander — read-only, no selection */}
      <div className="rounded border border-[var(--color-ide-border)]">
        <button
          className="w-full px-2 py-1.5 flex items-center gap-1.5 text-[11px] text-[var(--color-ide-text)] hover:bg-[var(--color-ide-surface-hover)] transition-colors"
          onClick={onToggleExpanded}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Rules that will run
          {rules && (
            <span className="text-[var(--color-ide-text-dim)]">
              ({rules.length})
            </span>
          )}
        </button>
        {expanded && (
          <div className="px-2 pb-2 flex flex-col gap-1">
            {rules === null ? (
              <div className="text-[10px] text-[var(--color-ide-text-dim)] py-1">
                Loading rules…
              </div>
            ) : (
              rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center gap-1.5 text-[11px] text-[var(--color-ide-text-muted)]"
                >
                  <span>{rule.label}</span>
                  {/* Custom hover tooltip: the native `title` attribute is
                      delayed ~half a second by the browser; this CSS
                      group-hover reveal shows the description immediately. */}
                  <span className="relative group cursor-help">
                    <HelpCircle size={10} />
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-0 bottom-full z-50 mb-1 w-48 rounded border border-[var(--color-ide-border)] bg-[var(--color-ide-surface)] px-2 py-1 text-[10px] leading-snug text-[var(--color-ide-text)] opacity-0 shadow-lg group-hover:opacity-100"
                    >
                      {rule.description}
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ViolationRow(props: {
  violation: SrvViolation;
  selected: boolean;
  onClick: () => void;
}) {
  const { violation, selected, onClick } = props;
  const ref = useRef<HTMLButtonElement>(null);

  // Keep a canvas-driven selection scrolled into view.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  // Rows stay clickable once an edit has made the run stale: fixing a violation
  // and jumping straight to the next one is the main way this list gets used,
  // and disabling the rows would force a re-run between every fix.
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`w-full px-2 py-1.5 rounded flex items-center gap-2 text-left transition-colors cursor-pointer ${
        selected
          ? "bg-red-500/20 border-l-2 border-red-400"
          : "bg-red-500/10 hover:bg-red-500/20 border-l-2 border-transparent"
      }`}
    >
      <span className="text-red-400">
        <XCircle size={12} />
      </span>
      <div className="flex-1 min-w-0 text-[11px] truncate">
        {violation.message}
      </div>
    </button>
  );
}

// --- helpers ---------------------------------------------------------------

interface RuleGroup {
  ruleId: string;
  items: Array<{ violation: SrvViolation; index: number }>;
}

// Group violations by rule id, preserving first-seen rule order and the
// original index of each violation (needed for focus/select correlation).
function groupByRule(
  violations: ReadonlyArray<SrvViolation>
): Array<RuleGroup> {
  const groups: Array<RuleGroup> = [];
  violations.forEach((violation, index) => {
    let group = groups.find((g) => g.ruleId === violation.ruleId);
    if (!group) {
      group = { ruleId: violation.ruleId, items: [] };
      groups.push(group);
    }
    group.items.push({ violation, index });
  });
  return groups;
}

function ruleLabel(
  rules: ReadonlyArray<{ id: string; label: string }> | null,
  ruleId: string
): string {
  return rules?.find((r) => r.id === ruleId)?.label ?? ruleId;
}
